import { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import { Routes, Route, NavLink, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Show, SignIn, SignUp, useAuth, useClerk } from "@clerk/react";
import { Users, Luggage, ListChecks, LayoutTemplate, FileText, Mail, UserCog, BookUser, LogOut, Loader2, Upload, Download, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Monogram } from "@/components/Monogram";
import { API, errMsg } from "@/lib/api";
import Families from "@/admin/Families";
import FamilyDetail from "@/admin/FamilyDetail";
import Arrivals from "@/admin/Arrivals";
import Fields from "@/admin/Fields";
import PagesCms from "@/admin/PagesCms";
import Content from "@/admin/Content";
import EmailSettings from "@/admin/EmailSettings";
import Organizers from "@/admin/Organizers";
import Directory from "@/admin/Directory";

const ApiCtx = createContext(null);
export const useApi = () => useContext(ApiCtx);

export const PageHead = ({ title, text, children }) => (
  <div className="mb-8 flex flex-col gap-5 2xl:flex-row 2xl:items-end 2xl:justify-between">
    <div>
      <h1 className="font-serif-d text-4xl sm:text-5xl" data-testid="admin-page-title">{title}</h1>
      {text && <p className="mt-2 max-w-xl text-[15px] text-slate-600">{text}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const UploadButton = ({ onUploaded, accept = "image/*", label = "Upload", multiple = false, testId = "upload-btn" }) => {
  const api = useApi();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        onUploaded(await api("post", "/upload", fd));
      }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      <button type="button" className="btn-ghost !py-2 text-sm" onClick={() => ref.current.click()} disabled={busy} data-testid={testId}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {label}
      </button>
    </>
  );
};

export const ExportMenu = ({ path, name, params = {}, testId = "export-btn" }) => {
  const api = useApi();
  const download = async (format) => {
    try {
      const blob = await api("get", path, null, { responseType: "blob", params: { ...params, format } });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.${format}`;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="btn-ghost !py-2 text-sm" data-testid={testId}><Download size={15} /> Export <ChevronDown size={14} /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuItem onClick={() => download("xlsx")} data-testid={`${testId}-xlsx`}>Excel (.xlsx)</DropdownMenuItem>
        <DropdownMenuItem onClick={() => download("csv")} data-testid={`${testId}-csv`}>CSV (.csv)</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NAV = [
  ["/admin", "Family & friends", Users, true], ["/admin/arrivals", "Arrivals", Luggage], ["/admin/fields", "Fields", ListChecks],
  ["/admin/pages", "Pages", LayoutTemplate], ["/admin/content", "Content", FileText], ["/admin/email", "Email", Mail],
  ["/admin/organizers", "Organizers", UserCog], ["/admin/lists", "Lists", BookUser],
];

const Shell = ({ org, children }) => {
  const { signOut } = useClerk();
  return (
    <div className="min-h-screen bg-[#F6F0E5]" data-testid="admin-shell">
      <header className="sticky top-0 z-40 border-b border-[var(--line)] bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2.5">
          <Link to="/admin" className="flex shrink-0 items-center gap-2" data-testid="admin-brand"><Monogram size={34} testId="admin-header-monogram" /><span className="font-script hidden text-2xl sm:inline">Yash &amp; Suhani</span></Link>
          <nav className="flex flex-1 gap-1 overflow-x-auto">
            {NAV.map(([to, label, Icon, end]) => (
              <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.split(" ")[0].toLowerCase()}`}
                className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm transition-colors ${isActive ? "bg-[var(--ink)] text-[var(--ivory)]" : "text-slate-600 hover:bg-[var(--ivory-2)]"}`}>
                <Icon size={15} /> <span className={end ? "" : "hidden md:inline"}>{label}</span>
              </NavLink>
            ))}
          </nav>
          <span className="hidden text-xs text-slate-400 xl:inline" data-testid="admin-email">{org.email}</span>
          <button onClick={() => signOut({ redirectUrl: "/admin" })} className="rounded-full p-2 text-slate-500 hover:bg-[var(--ivory-2)]" aria-label="Sign out" data-testid="admin-signout-btn"><LogOut size={17} /></button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:py-12">{children}</main>
    </div>
  );
};

const Denied = ({ msg }) => {
  const { signOut } = useClerk();
  return (
    <div className="flex min-h-screen items-center justify-center p-6" data-testid="admin-denied">
      <div className="ivory-card max-w-md p-10 text-center">
        <Monogram size={80} className="mx-auto" />
        <h1 className="font-serif-d mt-4 text-3xl">Organizers only</h1>
        <p className="mt-3 text-slate-600">{msg}</p>
        <button className="btn-ink mt-6" onClick={() => signOut({ redirectUrl: "/admin" })} data-testid="admin-denied-signout">Sign in with another account</button>
      </div>
    </div>
  );
};

const Authed = () => {
  const { getToken } = useAuth();
  const [org, setOrg] = useState(null);
  const [denied, setDenied] = useState("");
  const api = useCallback(async (method, path, data, opts = {}) => {
    const token = await getToken();
    const res = await axios({ method, url: `${API}/admin${path}`, data, headers: { Authorization: `Bearer ${token}` }, ...opts });
    return res.data;
  }, [getToken]);
  useEffect(() => {
    api("get", "/me").then(setOrg).catch((e) => setDenied(errMsg(e)));
  }, [api]);
  if (denied) return <Denied msg={denied} />;
  if (!org) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin text-[var(--gold)]" /></div>;
  return (
    <ApiCtx.Provider value={api}>
      <Shell org={org}>
        <Routes>
          <Route index element={<Families />} />
          <Route path="families/:id" element={<FamilyDetail />} />
          <Route path="arrivals" element={<Arrivals />} />
          <Route path="fields" element={<Fields />} />
          <Route path="pages" element={<PagesCms />} />
          <Route path="content" element={<Content />} />
          <Route path="email" element={<EmailSettings />} />
          <Route path="lists" element={<Directory />} />
          <Route path="organizers" element={<Organizers me={org} />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Shell>
    </ApiCtx.Provider>
  );
};

const EmailLink = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/organizer/email-link`, { email });
      setDone(data.message);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  if (done) return <p className="mt-6 max-w-sm rounded-xl bg-[var(--ivory-2)] p-4 text-center text-sm text-slate-700" data-testid="org-email-link-success">{done}</p>;
  if (!open) return <button onClick={() => setOpen(true)} className="mt-6 inline-flex items-center gap-2 text-sm text-[var(--wine)] underline-offset-4 hover:underline" data-testid="org-email-link-open-btn"><Mail size={15} /> Not using Google? Email me a sign-in link</button>;
  return (
    <form onSubmit={submit} className="mt-6 w-full max-w-sm space-y-3" data-testid="org-email-link-form">
      <label className="label-lux">Organizer email (Outlook, Microsoft 365, any provider)</label>
      <input type="email" required className="input-lux" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="org-email-link-input" />
      <button className="btn-ink w-full" disabled={busy} data-testid="org-email-link-submit-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Email me a sign-in link</button>
    </form>
  );
};

export function AdminSignIn({ mode }) {
  // Organizers can only create an account from the invitation link emailed when they are added.
  if (new URLSearchParams(window.location.search).has("__clerk_ticket")) sessionStorage.setItem("ys_invited", "1");
  const hasTicket = sessionStorage.getItem("ys_invited") === "1" || window.location.pathname.startsWith("/sign-up/");
  return (
    <div className="flex min-h-screen bg-[var(--ivory)]" data-testid="admin-login-page">
      <div className="grain relative hidden flex-1 flex-col items-center justify-center overflow-hidden bg-[var(--wine)] text-[var(--ivory)] lg:flex">
        <div className="rounded-full bg-[var(--ivory)] p-6"><Monogram size={200} testId="login-monogram" /></div>
        <div className="font-script mt-8 text-6xl">Yash &amp; Suhani</div>
        <div className="eyebrow mt-3 !text-[var(--gold)]">Organizer panel</div>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="mb-6 text-center lg:hidden">
          <Monogram size={96} className="mx-auto" testId="login-monogram-mobile" />
          <div className="eyebrow mt-3">Organizer panel</div>
        </div>
        {mode === "up"
          ? (hasTicket ? <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" fallbackRedirectUrl="/admin" /> : <Navigate to="/sign-in" replace />)
          : <SignIn routing="path" path="/sign-in" fallbackRedirectUrl="/admin" appearance={{ elements: { footerAction: { display: "none" } } }} />}
        {mode !== "up" && <EmailLink />}
        <p className="mt-6 max-w-sm text-center text-xs text-slate-500">Only emails added as organizers can open the panel.</p>
      </div>
    </div>
  );
}

export default function AdminApp() {
  return (
    <>
      <Show when="signed-in"><Authed /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
